import { useEffect, useMemo, useState } from "react";
import { Form, Field } from "react-final-form";
import DatePicker from "react-datepicker";
import QuillEditor from "lib/QuillEditor";
import MediaFinderContainer from "lib/MediaFinderContainer";
import TextBox from "components/TextBox";
import Uploader from "components/Uploader";
import FormRow from "components/MainDetails/FormRow";
import { apiUrl } from "config";
import { categoryMap } from "./common";
import styles from "./index.module.scss";

type FormValues = Omit<Article, "enrollment"> & { enrollment: Array<string> };

const enrollmentMap = {
  company: "公司名称",
  name: "姓名",
  mobile: "联系电话",
  email: "电子邮箱",
};

interface Props {
  id: string;
  item?: ArticleView;
  onSave: (item: Article) => Promise<any>;
}

const DetailsForm = ({ id, item, onSave }: Props) => {
  const [image, setImage] = useState<string>();
  const [video, setVideo] = useState<string>();

  const submit = async (values: FormValues) => {
    const { enrollment } = values;

    await onSave({
      ...values,
      image,
      video,
      enrollment: enrollment?.length ? enrollment.join(",") : "",
    });
  };

  const initialValues: Partial<FormValues> = useMemo(
    () => ({
      category: "notice",
      pinned: false,
      isPublished: false,
      ...item,
      enrollment: item?.enrollment ? item.enrollment.split(",") : [],
    }),
    [item]
  );

  useEffect(() => {
    if (item) {
      setImage(item.image);
      setVideo(item.video);
      return;
    }
    setImage("");
    setVideo("");
  }, [item]);

  return (
    <div className={styles.details}>
      <div className={styles.title}>{id ? "编辑" : "创建"}</div>

      <Form<FormValues>
        key={id}
        initialValues={initialValues}
        onSubmit={submit}
      >
        {({ handleSubmit, form, values, submitting }) => (
          <form className={styles.form} onSubmit={handleSubmit}>
            {id && <FormRow label="编号" control={id} />}

            <Field name="name" parse={(value) => value}>
              {({ input, meta }) => (
                <FormRow
                  label="名称"
                  control={<input {...input} type="text" placeholder="名称" />}
                  error={meta.touched && meta.error}
                />
              )}
            </Field>

            <Field name="category" parse={(value) => value}>
              {({ input, meta }) => (
                <FormRow
                  label="类别"
                  control={
                    <select {...input}>
                      <option />
                      {Object.entries(categoryMap).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </select>
                  }
                />
              )}
            </Field>
            {values.category === "enrollment" && (
              <FormRow
                label="报名字段"
                control={
                  <div className={styles.enrollment}>
                    {Object.entries(enrollmentMap).map(([key, value]) => (
                      <Field
                        key={key}
                        name="enrollment"
                        type="checkbox"
                        value={key}
                      >
                        {({ input }) => (
                          <label>
                            <input {...input} /> {value}
                          </label>
                        )}
                      </Field>
                    ))}
                  </div>
                }
              />
            )}

            <FormRow
              label="缩略图"
              control={
                image && (
                  <div className={styles.col}>
                    <img
                      className={styles.thumbnail}
                      src={`${apiUrl}/uploads/thumbnail/${image}`}
                      alt="thumbnail"
                    />
                    <div>{image}</div>
                  </div>
                )
              }
            />

            <FormRow
              label="上传图片"
              control={
                <Uploader
                  type="image"
                  dir="thumbnail"
                  onLoad={(_file, fileUrl) => setImage(fileUrl)}
                />
              }
            />

            <FormRow
              label="视频"
              control={
                video && (
                  <div className={styles.col}>
                    <video controls loop className={styles.video}>
                      <source
                        src={`${apiUrl}/uploads/thumbnail/${video}`}
                        type="video/mp4"
                      />
                    </video>
                    <div>{video}</div>
                  </div>
                )
              }
            />

            <FormRow
              label="上传视频"
              control={
                <Uploader
                  title="将视频文件拖动到此处，或点击选择文件"
                  type="video"
                  dir="thumbnail"
                  onLoad={(_file, fileUrl) => setVideo(fileUrl)}
                />
              }
            />

            <Field name="summary" parse={(value) => value}>
              {({ input, meta }) => (
                <FormRow
                  label="简介"
                  control={<TextBox {...input} placeholder="简介" />}
                  error={meta.touched && meta.error}
                />
              )}
            </Field>

            <Field name="content" parse={(value) => value}>
              {({ input, meta }) => (
                <FormRow
                  label="内容"
                  control={
                    <QuillEditor
                      value={input.value}
                      onChange={input.onChange}
                      placeholder="请输入内容"
                      mediaPicker={MediaFinderContainer}
                    />
                  }
                />
              )}
            </Field>

            <Field
              name="pinned"
              parse={(v) => v === "yes"}
              format={(v) => (v ? "yes" : "no")}
            >
              {({ input, meta }) => (
                <FormRow
                  label="置顶"
                  control={
                    <select {...input}>
                      <option value="no">未置顶</option>
                      <option value="yes">已置顶</option>
                    </select>
                  }
                  error={meta.touched && meta.error}
                />
              )}
            </Field>

            <Field name="isPublished" parse={(value) => value} type="checkbox">
              {({ input, meta }) => (
                <FormRow
                  label="状态"
                  control={
                    <label>
                      <input
                        {...input}
                        type="checkbox"
                        onChange={(e) => {
                          input.onChange(e);
                          // 当isPublished被勾中，并且publishedAt没有值的时候
                          // 设置一个默认的publishedAt值
                          if (!values.publishedAt && !values.isPublished)
                            form.change(
                              "publishedAt",
                              new Date().toISOString()
                            );
                        }}
                      />
                      发布
                    </label>
                  }
                  error={meta.touched && meta.error}
                />
              )}
            </Field>
            <Field name="publishedAt">
              {({ input, meta }) => (
                <FormRow
                  label="发布日期"
                  control={
                    <div className={styles["compact-control"]}>
                      <DatePicker
                        selected={
                          input.value &&
                          ["string", "number", "date"].includes(
                            typeof input.value
                          )
                            ? new Date(input.value)
                            : null
                        }
                        onChange={(date) => {
                          input.onChange({
                            target: {
                              value: date ? date.toISOString() : date,
                            },
                          });
                        }}
                        showTimeSelect
                        dateFormat="yyyy-MM-dd HH:mm"
                      />
                    </div>
                  }
                  error={meta.touched && meta.error}
                />
              )}
            </Field>

            <div className={styles.buttons}>
              <button color="secondary" onClick={() => form.reset()}>
                重置
              </button>
              <button color="primary" type="submit" disabled={submitting}>
                {submitting ? "提交中..." : "提交"}
              </button>
              {/* {hint && (
                    <span style={{ fontSize: "0.85em", color: "red" }}>{hint}</span>
                  )} */}
            </div>
          </form>
        )}
      </Form>
      {/* <pre>{JSON.stringify(currentItem, null, 2)}</pre> */}
    </div>
  );
};

export default DetailsForm;
